import { defineStore, storeToRefs } from "pinia";
import { nextTick, ref, watch } from "vue";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { useTenantFeatureAccess } from "@/composables/useTenantFeatureAccess";

export const useChatPanelStore = defineStore("chatPanel", () => {
  const isOpen = ref(false);

  // The skill pinned for the next outgoing message. It lives here rather than in ChatInput so that
  // both entry points share one mechanism: the "Skills" picker on the chat input, and the "Use in
  // chat" button on the AI Agent Skills cards, which has to set the skill from outside the panel
  // (RTLCA-4990). Only the name is kept — that is all the send path needs, since the skill is
  // invoked by shaping the outgoing text (see useAgentChat's submit).
  const activeSkillName = ref<string | null>(null);

  function setActiveSkill(skillName: string) {
    activeSkillName.value = skillName;
  }

  function clearActiveSkill() {
    activeSkillName.value = null;
  }

  function toggle() {
    if (isOpen.value) close();
    else open();
  }

  function open() {
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    // The panel resets its conversation on close (ChatPanel's @after-leave calls newChat), and the
    // pin is single-shot within one conversation, so it must not survive into the next one.
    // ChatInput's own clear-on-reset watcher cannot do this: it is already unmounted by then.
    clearActiveSkill();
  }

  // Route a skill into the chat from outside the panel — the "Use in chat" button on a skill card.
  // Pins the skill first so ChatInput paints the chip on the same frame the window appears.
  function openWithSkill(skillName: string) {
    setActiveSkill(skillName);
    open();
  }

  // Force the panel closed when a tenant change lands on an org without AI
  // entitlement. Watches user.tenantId (not canUseAiChat) so route-transition
  // false-positives from useTenantFeatureAccess's try/catch can't trigger us.
  // Lives in the store so it survives ChatPanel unmount/remount during the
  // switch, and so it fires in other tabs where the local isSwitchingTenant
  // flag never flips. Force-closes via close() rather than assigning isOpen so
  // a skill pinned in the previous tenant is dropped along with the window.
  const { user } = storeToRefs(useAuthStateStore());
  const { canUseAiChat } = useTenantFeatureAccess();

  watch(
    () => user.value?.tenantId,
    async (newId, oldId) => {
      if (newId === oldId) return;
      if (oldId === undefined) return;
      if (!newId) {
        close();
        return;
      }
      // One microtask lets Frontegg's entitlement computed reflect the new
      // tenant before we read canUseAiChat.
      await nextTick();
      if (!canUseAiChat.value) close();
    }
  );

  return { isOpen, activeSkillName, toggle, open, close, setActiveSkill, clearActiveSkill, openWithSkill };
});
